import { useState, useEffect } from 'react';
import { Activity, Mail, Phone, Calendar, StickyNote } from 'lucide-react';
import Header from '../components/layout/Header';
import { getActivities } from '../services/api';

const iconMap: Record<string, any> = {
  call: Phone,
  email: Mail,
  meeting: Calendar,
  note: StickyNote,
  task: Activity,
  sms: Mail,
  slack_message: Mail,
  voice_interaction: Phone,
};

const typeColors: Record<string, { bg: string; fg: string }> = {
  call: { bg: 'rgba(59,130,246,0.12)', fg: '#3b82f6' },
  email: { bg: 'rgba(139,92,246,0.12)', fg: '#8b5cf6' },
  meeting: { bg: 'rgba(245,158,11,0.12)', fg: '#f59e0b' },
  note: { bg: 'rgba(144,185,171,0.12)', fg: 'var(--netrun-green)' },
  task: { bg: 'rgba(144,185,171,0.12)', fg: 'var(--netrun-green)' },
  sms: { bg: 'rgba(139,92,246,0.12)', fg: '#8b5cf6' },
  slack_message: { bg: 'rgba(139,92,246,0.12)', fg: '#8b5cf6' },
  voice_interaction: { bg: 'rgba(59,130,246,0.12)', fg: '#3b82f6' },
};

function relativeTime(dateStr: string): string {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)}w ago`;
  return date.toLocaleDateString();
}

interface ActivityItem {
  id: string;
  type: string;
  subject: string;
  contact_name: string;
  description: string;
  created_at: string;
  completed: boolean;
}

export default function ActivitiesPage() {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await getActivities({ limit: '50' });
        const items = data?.items ?? data ?? [];
        setActivities(Array.isArray(items) ? items.map((a: any) => ({
          id: a.id,
          type: a.type || 'note',
          subject: a.subject || 'Activity',
          contact_name: a.contact_name || '',
          description: a.description || '',
          created_at: a.created_at || '',
          completed: a.is_completed ?? a.completed ?? false,
        })) : []);
      } catch (err) {
        console.error('Failed to load activities:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div className="page-enter">
      <Header title="Activities" subtitle={`${activities.length} interactions and tasks`} />
      <div className="p-4 md:p-8">
        {loading && (
          <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>Loading activities...</p>
        )}

        {!loading && activities.length === 0 && (
          <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>
            No activities yet. Log your first interaction.
          </p>
        )}

        <div className="space-y-3">
          {activities.map((act) => {
            const Icon = iconMap[act.type] || Activity;
            const colors = typeColors[act.type] || typeColors.note;
            return (
              <div key={act.id} className="flex gap-4 px-6 py-4 rounded-xl card-interactive"
                style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: colors.bg }}>
                  <Icon size={18} style={{ color: colors.fg }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-body" style={{ color: 'var(--text-primary)' }}>{act.subject}</span>
                    {act.completed && (
                      <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(34,197,94,0.1)', color: 'var(--success)' }}>Done</span>
                    )}
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {act.contact_name}{act.description ? ` -- ${act.description}` : ''}
                  </p>
                </div>
                <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                  {relativeTime(act.created_at)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
